import React, { useEffect, useMemo, useState } from 'react';
import { Navigate, Link } from 'react-router-dom';
import axios from 'axios';
import {
  ExternalLink, ChevronDown, Package, Loader2, RefreshCcw, Truck, MapPin, Check,
  ClipboardCheck, Upload, X as XIcon, Printer,
} from 'lucide-react';
import BranchShell from '../components/BranchShell';
import ReceivedDocumentsPanel from '../components/ReceivedDocumentsPanel';
import { getStatusAccent } from '../lib/orderStatus';
import { useBranchAuth, API } from '../context/BranchAuthContext';
import useBranchColumnHidden from '../hooks/useBranchColumnHidden';
import { useToast } from '../hooks/use-toast';
import { formatDateTime } from '../lib/dateFormat';

// All 10 order statuses the storefront/dispatch flow can enter.
// Feb 2026 — `confirmed_by_recipient` added so orders that have been
// edited by admin and then confirmed by the branch/customer render
// with a proper pill instead of falling through to a raw slug.
const STATUSES = ['draft', 'new', 'confirmed', 'confirmed_by_recipient', 'assigned', 'picked_up', 'out_for_delivery', 'delivered', 'received', 'failed', 'cancelled'];
// Feb 2026 — status pill palette (per ops brief). Kept in sync with
// AdminOrders.jsx: confirmed→blue, new/draft→grey (PENDING),
// confirmed_by_recipient→teal (recipient signed off on the edit),
// assigned→yellow (PROCESSING), picked_up→orange,
// out_for_delivery→purple (DISPATCHED), delivered/received→green,
// cancelled/failed→red.
const statusChip = {
  draft: 'bg-neutral-200 text-neutral-700 border-neutral-300',
  new: 'bg-neutral-200 text-neutral-700 border-neutral-300',
  confirmed: 'bg-blue-100 text-blue-700 border-blue-200',
  confirmed_by_recipient: 'bg-teal-100 text-teal-700 border-teal-200',
  assigned: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  picked_up: 'bg-orange-100 text-orange-700 border-orange-200',
  out_for_delivery: 'bg-purple-100 text-purple-700 border-purple-200',
  delivered: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  received: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  failed: 'bg-red-100 text-red-700 border-red-200',
  cancelled: 'bg-red-100 text-red-700 border-red-200',
};

// Feb 2026 — the "Order Received" button appears ONLY when the order
// has actually been delivered. Earlier statuses like `picked_up` or
// `out_for_delivery` are still in transit and letting a branch mark
// them as received produced misleading receipt records.
const RECEIVE_ELIGIBLE = new Set(['delivered']);

/**
 * BranchOrders — "My Orders" page inside the /branch portal.
 * Data comes from `/api/branch/orders` which does server-side scoping
 * by customer_number — the frontend NEVER sees other branches' orders.
 * Route: /branch/orders
 */
const BranchOrders = () => {
  const { token, authHeaders, logout } = useBranchAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [receiveTarget, setReceiveTarget] = useState(null); // order being confirmed
  // Feb 2026 — inline-edit confirmation UI state (must be declared before
  // any conditional return to satisfy React's Rules of Hooks).
  const [confirmingEdits, setConfirmingEdits] = useState({}); // orderId -> bool
  const { toast } = useToast();
  // Admin-controlled column visibility (Feb 2026 v4). `isHidden("orders.status")`
  // returns true when the super admin has turned that column off for
  // every branch user.
  const { isHidden } = useBranchColumnHidden();

  useEffect(() => { document.title = 'Branch Portal — My Orders'; }, []);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/branch/orders`, { headers: authHeaders });
      setOrders(data.orders || []);
    } catch (err) {
      if (err?.response?.status === 401 || err?.response?.status === 403) {
        logout();
        return;
      }
      toast({ title: 'Failed to load orders', description: err?.response?.data?.detail || err?.message });
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (token) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const filtered = useMemo(() => {
    if (statusFilter === 'all') return orders;
    return orders.filter((o) => o.status === statusFilter);
  }, [orders, statusFilter]);

  const totals = useMemo(() => ({ count: orders.length }), [orders]);

  if (!token) return <Navigate to="/branch/login" replace />;

  /**
   * Trigger the order-summary PDF (items only, no prices). `window.open`
   * cannot attach an Authorization header on a top-level navigation, so
   * we append the branch JWT as a query-string parameter. The backend
   * endpoint accepts either transport (see `require_admin_or_branch_flex`).
   */
  const handlePrintOrder = (o) => {
    if (!token) return;
    const url = `${API}/admin/orders/${o.id}/order-summary.pdf?token=${encodeURIComponent(token)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  // Feb 2026 — inline-edit confirmation flow. When admin/staff edits an
  // order via the inline editor on Admin Orders, the branch sees a
  // prominent "Confirm Order" banner + button on their card. Clicking
  // it advances the order to `confirmed_by_recipient` and clears the
  // pending flag.
  const confirmOrderEdits = async (o) => {
    setConfirmingEdits((p) => ({ ...p, [o.id]: true }));
    try {
      await axios.post(
        `${API}/branch/orders/${o.id}/confirm-edits`, {},
        { headers: authHeaders },
      );
      toast({ title: 'Order confirmed', description: `Order #${o.order_number || o.id.slice(0, 8)} confirmed.` });
      await load();
    } catch (err) {
      toast({ title: 'Confirm failed', description: err?.response?.data?.detail || 'Try again.' });
    } finally {
      setConfirmingEdits((p) => ({ ...p, [o.id]: false }));
    }
  };

  return (
    <BranchShell>
      <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
        <div>
          <h1 className="font-display text-2xl font-bold text-[#0F172A]">My Orders</h1>
          <div className="text-xs text-neutral-500 mt-1" data-testid="branch-order-summary">
            {loading ? 'Loading orders…' : `${totals.count} order${totals.count === 1 ? '' : 's'}`}
          </div>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="text-xs font-semibold text-[#0078D4] hover:bg-[#DEECF9] px-3 py-1.5 rounded-full inline-flex items-center gap-1"
          data-testid="branch-refresh-btn"
        >
          <RefreshCcw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      {/* Status filter chips */}
      <div className="flex gap-1.5 flex-wrap mb-4" data-testid="branch-status-filters">
        <button
          type="button"
          onClick={() => setStatusFilter('all')}
          className={`px-3 py-1 rounded-full text-xs font-semibold ${statusFilter === 'all' ? 'bg-[#0F172A] text-white' : 'bg-white border border-neutral-200 text-neutral-700'}`}
          data-testid="branch-filter-all"
        >
          All ({orders.length})
        </button>
        {STATUSES.map((s) => {
          const count = orders.filter((o) => o.status === s).length;
          return (
            <button
              key={s}
              type="button"
              onClick={() => setStatusFilter(s)}
              className={`px-3 py-1 rounded-full text-xs font-semibold capitalize ${statusFilter === s ? 'bg-[#0078D4] text-white' : 'bg-white border border-neutral-200 text-neutral-700'}`}
              data-testid={`branch-filter-${s}`}
            >
              {s.replace('_', ' ')} ({count})
            </button>
          );
        })}
      </div>

      <div className="space-y-3">
        {loading && orders.length === 0 && (
          <div className="rounded-2xl bg-white border border-neutral-200 p-8 text-center text-sm text-neutral-500 flex items-center justify-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        )}
        {!loading && filtered.length === 0 && (
          <div className="rounded-2xl bg-white border border-neutral-200 p-10 text-center">
            <Package className="w-10 h-10 text-neutral-300 mx-auto mb-3" />
            <p className="text-neutral-500 text-sm">No orders to show.</p>
            <Link to="/branch" className="inline-block mt-3 text-xs font-semibold text-[#0078D4] hover:underline">
              Place your first order →
            </Link>
          </div>
        )}
        {filtered.map((o) => {
          const isOpen = expanded === o.id;
          const accent = getStatusAccent(o.status);
          return (
            <div
              key={o.id}
              className={`rounded-2xl border border-neutral-200 border-l-4 ${accent.border} ${accent.tint} overflow-hidden`}
              data-testid={`branch-order-${o.id}`}
            >
              <div
                className="p-4 flex flex-wrap items-center gap-3 cursor-pointer hover:bg-white/40"
                onClick={() => setExpanded(isOpen ? null : o.id)}
              >
                <div className="flex-1 min-w-[160px]">
                  {!isHidden('orders.order_number') && (
                    <div className="font-semibold text-[#0F172A] flex items-center gap-2 flex-wrap">
                      Order #{o.order_number || o.id.slice(0, 8)}
                      {!isHidden('orders.requisition_badge') && o.order_type === 'requisition' && (
                        <span className="inline-block bg-indigo-100 text-indigo-800 border border-indigo-200 rounded-full px-2 py-0.5 text-[10px] font-bold tracking-wider uppercase">
                          Requisition
                        </span>
                      )}
                    </div>
                  )}
                  {!isHidden('orders.created_at') && (
                    <div className="text-xs text-neutral-500 mt-1">
                      {formatDateTime(o.created_at)}
                    </div>
                  )}
                  {/* Feb 2026 v10 — comma-joined category list drawn
                      from the order's items so branch staff spot the
                      order's "mix" at a glance without expanding it. */}
                  {(o.categories || []).length > 0 && (
                    <div
                      className="text-[11px] text-neutral-500 mt-0.5 truncate"
                      data-testid={`branch-order-categories-${o.id}`}
                      title={(o.categories || []).join(', ')}
                    >
                      {(o.categories || []).join(', ')}
                    </div>
                  )}
                </div>
                {!isHidden('orders.total_items') && (
                  <div className="text-sm text-neutral-600 tabular-nums">
                    {(o.items || []).reduce((s, i) => s + Number(i.qty || 0), 0)} items
                  </div>
                )}
                {!isHidden('orders.allocated') && (
                  <div
                    className="text-xs text-neutral-500 tabular-nums"
                    data-testid={`branch-order-allocated-${o.id}`}
                    title="Total qty allocated to warehouse placements for this order"
                  >
                    <span className="font-semibold text-[#0F172A]">{Number(o.allocated_qty || 0)}</span>
                    <span className="text-neutral-400"> allocated</span>
                  </div>
                )}
                {!isHidden('orders.status') && (
                  <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider border ${statusChip[o.status] || statusChip.new}`} data-testid={`branch-order-status-${o.id}`}>
                    {String(o.status || 'new').replace(/_/g, ' ')}
                  </span>
                )}
                {RECEIVE_ELIGIBLE.has(o.status) && (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setReceiveTarget(o); }}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-emerald-600 hover:bg-emerald-700 text-white transition"
                    data-testid={`branch-order-received-btn-${o.id}`}
                  >
                    <ClipboardCheck className="w-3.5 h-3.5" /> Order Received
                  </button>
                )}
                <ChevronDown className={`w-5 h-5 text-neutral-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
              </div>

              {isOpen && (
                <div className="px-4 pb-4 border-t border-neutral-100 pt-3">
                  {/* Feb 2026 — Confirm Order banner. Shows when admin/
                      staff edited the order inline; the branch must
                      review + confirm before the workflow resumes. */}
                  {o.edit_pending_confirmation && (
                    <div
                      className="mb-3 rounded-xl border-2 border-amber-400 bg-amber-50 px-4 py-3"
                      data-testid={`branch-order-${o.id}-confirm-edit-banner`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-bold text-amber-900">
                            This order was edited by admin — please review and confirm
                          </div>
                          <div className="text-[11px] text-amber-800 mt-0.5">
                            Check the updated items and total below. Until you click Confirm, this order stays paused.
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); confirmOrderEdits(o); }}
                          disabled={!!confirmingEdits[o.id]}
                          className="rounded-full bg-amber-600 hover:bg-amber-700 disabled:opacity-60 text-white px-4 py-2 text-xs font-bold whitespace-nowrap"
                          data-testid={`branch-order-${o.id}-confirm-edit-btn`}
                        >
                          {confirmingEdits[o.id] ? 'Confirming…' : 'Confirm Order'}
                        </button>
                      </div>
                    </div>
                  )}
                  {/* Detail-header actions — Print button downloads the
                      items-only order summary PDF (no prices). Branch users
                      do NOT see the pick list at all — that view is admin/
                      warehouse-only. */}
                  <div className="flex flex-wrap items-center gap-2 mb-3">
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); handlePrintOrder(o); }}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-[#0078D4] hover:bg-[#005A9E] text-white transition"
                      data-testid={`branch-print-order-btn-${o.id}`}
                    >
                      <Printer className="w-3.5 h-3.5" /> Print
                    </button>
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <div className="text-[10px] font-bold text-neutral-500 uppercase tracking-wider mb-1.5">Items</div>
                      <div className="space-y-2 text-sm" data-testid={`branch-order-${o.id}-items-table`}>
                        <div className="overflow-x-auto rounded-lg border border-neutral-200">
                          <table className="w-full text-left">
                            <thead>
                              <tr className="bg-neutral-50 text-[10px] uppercase tracking-wide text-neutral-500">
                                <th className="px-3 py-2 font-semibold w-10 text-right tabular-nums">#</th>
                                <th className="px-3 py-2 font-semibold w-[110px]">Item Code</th>
                                <th className="px-3 py-2 font-semibold">Item Name</th>
                                <th className="px-3 py-2 font-semibold w-[110px]">Pack</th>
                                <th className="px-3 py-2 font-semibold text-right w-[90px]">Qty</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(o.items || []).map((i, idx) => (
                                <tr key={idx} className="border-t border-neutral-100 align-top">
                                  <td className="px-3 py-2 text-right tabular-nums text-neutral-500" data-testid={`branch-order-${o.id}-line-no-${idx}`}>{(i?.line_no != null && i?.line_no !== '') ? i.line_no : (idx + 1)}</td>
                                  <td className="px-3 py-2 font-mono text-xs text-neutral-600">{i.sku || '—'}</td>
                                  <td className="px-3 py-2 text-[#0F172A]">
                                    {i.name}
                                  </td>
                                  <td className="px-3 py-2 text-neutral-600 tabular-nums" data-testid={`branch-order-${o.id}-items-pack-${idx}`}>
                                    {i.size || '—'}
                                  </td>
                                  <td className="px-3 py-2 tabular-nums text-right whitespace-nowrap">
                                    {i.qty} <span className="text-neutral-500">{i.uom || 'ctn'}</span>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        <div className="text-[11px] text-neutral-500 uppercase tracking-wider font-semibold">
                          {(o.items || []).reduce((s, i) => s + Number(i.qty || 0), 0)} items in this order
                        </div>
                      </div>
                    </div>
                    <div>
                      <div className="text-[10px] font-bold text-neutral-500 uppercase tracking-wider mb-1.5">Delivery status</div>
                      <StatusTimeline order={o} />
                      <div className="mt-3 flex flex-wrap items-center gap-2">
                        {o.order_number && (
                          <Link
                            to={`/track/${o.order_number}`}
                            target="_blank"
                            rel="noreferrer"
                            className="text-xs font-semibold text-[#0078D4] hover:bg-[#DEECF9] px-3 py-1.5 rounded-full inline-flex items-center gap-1"
                            data-testid={`branch-track-link-${o.id}`}
                          >
                            <ExternalLink className="w-3.5 h-3.5" /> Live tracking page
                          </Link>
                        )}
                        {o.location_lat && o.location_lng && (
                          <a
                            href={`https://www.google.com/maps?q=${o.location_lat},${o.location_lng}`}
                            target="_blank"
                            rel="noreferrer"
                            className="text-xs font-semibold text-emerald-700 bg-emerald-50 hover:bg-emerald-100 px-3 py-1.5 rounded-full inline-flex items-center gap-1"
                          >
                            <MapPin className="w-3.5 h-3.5" /> Delivery location
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                  {!isHidden('orders.notes') && o.notes && (
                    <div className="mt-3 text-xs text-neutral-600 bg-neutral-50 rounded-lg px-3 py-2">
                      <span className="font-semibold text-neutral-500 uppercase tracking-wider text-[10px]">Notes:</span> {o.notes}
                    </div>
                  )}
                  {/* Received Documents — visible when admin has
                      allowed this branch user to see them. Server
                      strips the array to [] otherwise; we hide the
                      whole section in that case so branches don't
                      see an empty placeholder. */}
                  {o.erp_invoice_number && (
                    <div
                      className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-800"
                      data-testid={`branch-order-${o.id}-erp-invoice`}
                    >
                      <span className="uppercase tracking-wider text-[10px] mr-2">ERP Invoice #</span>
                      <span className="font-mono">{o.erp_invoice_number}</span>
                    </div>
                  )}
                  {(o.received_documents || []).length > 0 && (
                    <ReceivedDocumentsPanel
                      docs={o.received_documents || []}
                      baseUrl={`${API}/branch/orders/${o.id}/received-documents`}
                      authHeaders={authHeaders}
                      testidPrefix={`branch-order-${o.id}`}
                    />
                  )}
                  {(o.loading_photos || []).length > 0 && (
                    <ReceivedDocumentsPanel
                      docs={o.loading_photos || []}
                      baseUrl={`${API}/branch/orders/${o.id}/loading-photos`}
                      authHeaders={authHeaders}
                      testidPrefix={`branch-order-${o.id}-loading`}
                      label="Loading photos"
                    />
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
      {receiveTarget && (
        <OrderReceivedModal
          order={receiveTarget}
          authHeaders={authHeaders}
          onClose={() => setReceiveTarget(null)}
          onDone={(updatedOrder) => {
            setOrders((prev) => prev.map((x) => x.id === updatedOrder.id ? { ...x, ...updatedOrder } : x));
            setReceiveTarget(null);
            toast({ title: 'Receipt confirmed', description: `Order ${updatedOrder.order_number || updatedOrder.id.slice(0, 8)} marked as received. Confirmation email sent.` });
          }}
        />
      )}
    </BranchShell>
  );
};

const StatusTimeline = ({ order }) => {
  const steps = [
    { key: 'new', label: 'Order placed', at: order.created_at, icon: Package },
    { key: 'confirmed', label: 'Confirmed', at: order.confirmed_at, icon: Check },
    { key: 'assigned', label: 'Driver assigned', at: order.driver_assigned_at, icon: Truck },
    { key: 'picked_up', label: 'Picked up', at: order.picked_up_at, icon: Truck },
    { key: 'out_for_delivery', label: 'Out for delivery', at: order.out_for_delivery_at, icon: Truck },
    { key: 'delivered', label: 'Delivered', at: order.delivered_at, icon: Check },
    ...(order.status === 'failed' ? [{ key: 'failed', label: 'Delivery failed', at: order.failed_at, icon: Package }] : []),
    ...(order.status === 'cancelled' ? [{ key: 'cancelled', label: 'Cancelled', at: null, icon: Package }] : []),
  ];
  const currentIdx = steps.findIndex((s) => s.key === order.status);
  return (
    <ol className="relative pl-5 space-y-2">
      {steps.map((s, i) => {
        const reached = i <= currentIdx || !!s.at;
        const Icon = s.icon;
        return (
          <li key={s.key} className="relative">
            <span
              className={`absolute -left-5 top-0.5 w-3 h-3 rounded-full border-2 ${reached ? 'bg-[#0078D4] border-[#0078D4]' : 'bg-white border-neutral-300'}`}
              aria-hidden
            />
            <div className={`text-xs ${reached ? 'text-[#0F172A] font-semibold' : 'text-neutral-400'} flex items-center gap-1.5`}>
              <Icon className="w-3 h-3" />
              <span>{s.label}</span>
              {s.at && <span className="text-neutral-500 font-normal ml-1">· {formatDateTime(s.at)}</span>}
            </div>
          </li>
        );
      })}
    </ol>
  );
};

export default BranchOrders;

// -----------------------------------------------------------------------------
// OrderReceivedModal — branch confirms delivery with optional attachments.
// Submits multipart form-data to `/api/branch/orders/<id>/mark-received`;
// backend stores the files against the order + emails a confirmation
// (with the same attachments) to both the branch mailbox and the admin.
// -----------------------------------------------------------------------------
const ACCEPTED_MIMES = ['image/jpeg', 'image/png', 'application/pdf'];
const ACCEPT_ATTR = 'image/jpeg,image/jpg,image/png,application/pdf,.jpg,.jpeg,.png,.pdf';
const MAX_FILE_MB = 10;
const MAX_FILES = 10;

const OrderReceivedModal = ({ order, authHeaders, onClose, onDone }) => {
  const [files, setFiles] = useState([]);
  const [palletsReceived, setPalletsReceived] = useState(
    order.pallets_sent != null ? String(order.pallets_sent) : ''
  );
  const [palletNotes, setPalletNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState('');

  const addFiles = (list) => {
    setErr('');
    const incoming = Array.from(list || []);
    const next = [...files];
    for (const f of incoming) {
      if (next.length >= MAX_FILES) {
        setErr(`You can attach up to ${MAX_FILES} files.`);
        break;
      }
      if (!ACCEPTED_MIMES.includes(f.type) && !/\.(jpe?g|png|pdf)$/i.test(f.name)) {
        setErr(`Unsupported file "${f.name}". Allowed: JPG, PNG, PDF.`);
        continue;
      }
      if (f.size > MAX_FILE_MB * 1024 * 1024) {
        setErr(`"${f.name}" exceeds the ${MAX_FILE_MB} MB limit.`);
        continue;
      }
      next.push(f);
    }
    setFiles(next);
  };

  const removeFile = (i) => setFiles((prev) => prev.filter((_, idx) => idx !== i));

  const submit = async () => {
    setSubmitting(true);
    setErr('');
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append('files', f));
      if (palletsReceived !== '' && !Number.isNaN(parseInt(palletsReceived, 10))) {
        fd.append('pallets_received', String(parseInt(palletsReceived, 10)));
      }
      if (palletNotes.trim()) {
        fd.append('pallet_notes', palletNotes.trim());
      }
      const { data } = await axios.post(
        `${API}/branch/orders/${order.id}/mark-received`,
        fd,
        { headers: { ...authHeaders, 'Content-Type': 'multipart/form-data' } },
      );
      onDone({
        ...order,
        status: 'received',
        received_at: data.received_at,
        received_documents: data.received_documents,
      });
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || 'Something went wrong.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="order-received-modal"
    >
      <div
        className="bg-white rounded-2xl max-w-lg w-full shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-neutral-200 flex items-center justify-between">
          <div>
            <div className="text-lg font-bold text-[#0F172A]">Confirm receipt</div>
            <div className="text-xs text-neutral-500">
              Order #{order.order_number || order.id.slice(0, 8)}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-neutral-400 hover:text-neutral-700"
            aria-label="Close"
            data-testid="order-received-close-btn"
          >
            <XIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4">
          <p className="text-sm text-neutral-600">
            Attach any supporting documents (photos of the goods, signed stock
            receipt, etc.) and confirm to mark this order as received.
          </p>

          {/* Feb 2026 — Pallet Ledger Phase 2. When warehouse recorded a
              pallets_sent count, prompt the branch for their own count so
              the ledger can reconcile any discrepancy. */}
          {(order.pallets_sent != null && order.pallets_sent > 0) && (
            <div
              className="rounded-xl bg-indigo-50/60 border border-indigo-200 p-3 space-y-2"
              data-testid="order-received-pallets-block"
            >
              <div className="flex items-baseline justify-between">
                <div className="text-sm font-semibold text-indigo-900">Pallets received</div>
                <div className="text-[11px] text-indigo-700">
                  Warehouse sent <b>{order.pallets_sent}</b>
                </div>
              </div>
              <input
                type="number"
                min="0"
                value={palletsReceived}
                onChange={(e) => setPalletsReceived(e.target.value)}
                className="w-full h-10 rounded-md border border-indigo-200 bg-white px-3 text-sm"
                placeholder={`e.g. ${order.pallets_sent}`}
                data-testid="order-received-pallets-input"
              />
              {palletsReceived !== '' && Number(palletsReceived) !== Number(order.pallets_sent) && (
                <div
                  className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-2 py-1"
                  data-testid="order-received-pallets-discrepancy"
                >
                  ⚠ Discrepancy: you&apos;re acknowledging <b>{palletsReceived}</b> pallets vs
                  warehouse&apos;s <b>{order.pallets_sent}</b>. Add a note below explaining the difference.
                </div>
              )}
              <input
                value={palletNotes}
                onChange={(e) => setPalletNotes(e.target.value)}
                placeholder="Notes (damage, shortages, condition...)"
                className="w-full h-9 rounded-md border border-indigo-200 bg-white px-3 text-xs"
                data-testid="order-received-pallets-notes"
              />
            </div>
          )}

          <div>
            <div className="text-sm font-semibold text-[#0F172A] mb-2">
              Attach Documents (e.g. goods photos, stock receipt)
            </div>
            <label
              htmlFor="order-received-file-input"
              className="flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-neutral-300 bg-neutral-50 hover:bg-neutral-100 transition cursor-pointer py-6 text-center"
              data-testid="order-received-dropzone"
            >
              <Upload className="w-6 h-6 text-neutral-400" />
              <div className="text-sm text-neutral-600">
                <span className="font-semibold text-[#0078D4]">Click to upload</span>{' '}
                or drag & drop
              </div>
              <div className="text-[11px] text-neutral-400">
                JPG · PNG · PDF · up to {MAX_FILE_MB} MB per file · max {MAX_FILES} files
              </div>
              <input
                id="order-received-file-input"
                type="file"
                accept={ACCEPT_ATTR}
                multiple
                className="hidden"
                onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }}
                data-testid="order-received-file-input"
              />
            </label>

            {files.length > 0 && (
              <ul className="mt-3 space-y-1.5" data-testid="order-received-file-list">
                {files.map((f, i) => (
                  <li
                    key={i}
                    className="flex items-center gap-2 rounded-lg bg-neutral-50 border border-neutral-200 px-3 py-1.5 text-xs text-neutral-700"
                  >
                    <Package className="w-3.5 h-3.5 text-neutral-400" />
                    <span className="truncate flex-1">{f.name}</span>
                    <span className="text-neutral-400 tabular-nums">
                      {(f.size / 1024).toFixed(1)} KB
                    </span>
                    <button
                      type="button"
                      onClick={() => removeFile(i)}
                      className="text-neutral-400 hover:text-red-600"
                      aria-label={`Remove ${f.name}`}
                    >
                      <XIcon className="w-3.5 h-3.5" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {err && (
            <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid="order-received-error">
              {err}
            </div>
          )}
        </div>

        <div className="px-5 py-4 bg-neutral-50 border-t border-neutral-200 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2 rounded-lg text-sm font-semibold text-neutral-600 hover:bg-neutral-200 transition disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={submitting}
            className="px-4 py-2 rounded-lg text-sm font-semibold bg-emerald-600 hover:bg-emerald-700 text-white inline-flex items-center gap-2 transition disabled:opacity-50"
            data-testid="order-received-confirm-btn"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ClipboardCheck className="w-4 h-4" />}
            {submitting ? 'Confirming…' : 'Confirm receipt'}
          </button>
        </div>
      </div>
    </div>
  );
};
